import { useMemo } from 'react';
import { CAR_NAMES_MANAGEMENT_URL } from '../../constants/constants';
import { csvTextToFile } from './utils/csv';

export const fetchNewCarsBulk = async (
  pageSize,
  page,
  orderByField,
  orderDesc,
  filters
) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('buffer', pageSize);

    if (orderByField) {
      params.append('orderBy', orderByField);
    }

    if (orderDesc) {
      params.append('orderDesc', orderDesc);
    }

    const response = await fetch(
      `${CAR_NAMES_MANAGEMENT_URL}/new-cars-aggregate?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filters })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('validateLink Error:', error);
    throw error;
  }
};

export const saveUpdatedCars = async (updatedCar, updatedField) => {
  try {
    const response = await fetch(
      `${CAR_NAMES_MANAGEMENT_URL}/update-car-data`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updatedCar,
          updatedField
        })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('validateLink Error:', error);
    throw error;
  }
};

export const syncBqToPgNames = async (basePath, token) => {
  try {
    const response = await fetch(
      `${CAR_NAMES_MANAGEMENT_URL}${basePath}/sync`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error running scheduler:', error);
    throw error;
  }
};

export const download = async (basePath, token) => {
  try {
    const response = await fetch(
      `${CAR_NAMES_MANAGEMENT_URL}${basePath}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'cars_data.csv'; // fallback filename

    if (contentDisposition) {
      // Handle both quoted and unquoted filenames
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    return blob;
  } catch (error) {
    console.error('Error downloading cars data:', error);
    throw error;
  }
};

export const downloadCsvString = async (basePath, token) => {
  try {
    const response = await fetch(
      `${CAR_NAMES_MANAGEMENT_URL}${basePath}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.text();
    return data;
  } catch (error) {
    console.error('Error downloading cars data:', error);
    throw error;
  }
};

export const uploadCsv = async (basePath, file, token) => {
  try {
    const formData = new FormData();
    formData.append('csvFile', file);

    const response = await fetch(
      `${CAR_NAMES_MANAGEMENT_URL}${basePath}/upload`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error uploading CSV file:', error);
    throw error;
  }
};

const uploadCsvString = async (basePath, csvContent, token) => {
  const file = csvTextToFile(csvContent, 'edited.csv');
  return uploadCsv(basePath, file, token);
};

export const useApi = (basePath, token) => {
  return useMemo(
    () => ({
      syncBqToPgNames: () => syncBqToPgNames(basePath, token),
      download: () => download(basePath, token),
      downloadCsvString: () => downloadCsvString(basePath, token),
      uploadCsvString: (csvContent) =>
        uploadCsvString(basePath, csvContent, token),
      uploadCsv: (file) => uploadCsv(basePath, file, token)
    }),
    [basePath, token]
  );
};
